/* =============================================================
   DIALOG CONTENT — Separate file for easy editing
   Keys must match dialogKey props in App.js data constants.
   ============================================================= */

export const DIALOG_CONTENT = {
  /* ---- Moat / Why Us ---- */
  'data-compounding': {
    title: 'The Data Compounding Effect',
    subtitle: 'Your health intelligence grows exponentially',
    body: 'Every symptom you log, every medication you track, every report you upload makes Richie smarter about YOUR specific health. Unlike generic health apps that treat each data point in isolation, Richie builds a living, interconnected health graph that deepens every single day.',
    highlights: [
      'After 30 days — Richie identifies your personal health patterns',
      'After 90 days — more context than your entire medical record history',
      'After 1 year — an irreplaceable, comprehensive health intelligence profile',
      'Every data point cross-references with family history, environment, and medications',
    ],
    footer: 'The longer you use RichHealth.ai, the more indispensable it becomes — not because of lock-in, but because of genuine, compounding value.',
  },
  'family-network': {
    title: 'The Family Network Effect',
    subtitle: 'Each member multiplies intelligence for everyone',
    body: 'When your mother joins and logs her Type 2 diabetes, Richie immediately factors that into YOUR risk assessment. When your father adds his cardiac history, your child\'s health predictions improve. This isn\'t just data sharing — it\'s exponential health intelligence that no other platform offers.',
    highlights: [
      '1 member = baseline intelligence',
      '2 members = 4x cross-referential insights',
      '4 members = 16x hereditary pattern detection',
      'Each new connection strengthens the entire family\'s health safety net',
      'Selective sharing — you control exactly what Richie can see',
    ],
    footer: 'No other health platform in the world offers family-connected AI. This is patent-worthy innovation that changes how families manage health.',
  },
  'multi-model': {
    title: 'Multi-Model AI Architecture',
    subtitle: '15+ AI models working in concert',
    body: 'Richie isn\'t dependent on a single AI provider. Our cascade fallback system orchestrates 15+ models — including Gemini, Llama, DeepSeek, and Qwen — routing each health query to the model best suited to answer it. This means you always get the best possible response.',
    highlights: [
      '99.9% uptime through automatic model failover',
      'No vendor lock-in — always the best model for the job',
      'Specialized routing: clinical queries, dietary advice, mental health — each goes to the optimal model',
      'Continuous model evaluation ensures quality improves over time',
    ],
    footer: 'While other health apps are limited to one AI, RichHealth.ai harnesses the collective intelligence of the world\'s leading models.',
  },
  'doctor-triangle': {
    title: 'The Doctor-Patient-AI Triangle',
    subtitle: 'AI intelligence, verified by real doctors',
    body: 'Richie\'s AI analysis doesn\'t exist in a vacuum. Licensed doctors on our platform review AI-generated insights, verify report analyses, and provide clinical oversight. This creates a trust layer that pure AI platforms simply cannot replicate — combining the speed of AI with the judgment of experienced physicians.',
    highlights: [
      'Richie analyzes your health data instantly',
      'Doctors verify critical findings for clinical accuracy',
      'You receive both speed and confidence in every insight',
      'Continuous feedback loop improves Richie\'s accuracy over time',
    ],
    footer: 'This triangle of AI speed, doctor expertise, and patient data creates healthcare that is faster, smarter, and more trustworthy than any single approach.',
  },
  'environmental': {
    title: 'Environmental Health Intelligence',
    subtitle: 'The air you breathe is part of your health record',
    body: 'India loses 1.67 million lives to air pollution annually. RichHealth.ai is the first health platform to treat your environment as a core health variable. Richie tracks your location\'s AQI in real-time, builds a long-term exposure profile, and correlates environmental data with your symptoms and conditions.',
    highlights: [
      'Real-time AQI monitoring using US & China standards',
      'PM2.5, PM10, O\u2083 tracking with health impact scoring',
      'Correlation engine: your symptoms vs. pollution spikes',
      'Long-term environmental exposure risk assessment',
      'Location-based health news and disease outbreak alerts',
    ],
    footer: 'In a country where air quality directly impacts hundreds of millions, this feature alone is a market maker.',
  },

  /* ---- India-First Proprietary AI ---- */
  'india-first': {
    title: 'India-First Proprietary AI',
    subtitle: 'The only health AI built from the ground up for Indian healthcare',
    body: 'Richie isn\'t a Western health AI with an Indian skin. It\'s a proprietary model trained on Indian medical terminology, doctor-patient communication styles, regional disease patterns, local medication brands, and dietary contexts that no global AI understands. When a doctor in Delhi writes "Tab Crocin 650 TDS," Richie doesn\'t just parse it — it understands the clinical intent, the dosage pattern, and the cultural context behind it.',
    highlights: [
      'Trained on Indian prescription language, abbreviations, and doctor handwriting patterns',
      'Understands Indian medication brands — Crocin, Dolo-650, Shelcal, Thyronorm — not just generic names',
      'Regional disease awareness: dengue in Mumbai, TB in UP, thyroid disorders in coastal regions',
      'Dietary intelligence: dal-chawal nutrition profiles, ghee consumption context, regional food habits',
      'Bilingual medical understanding across Hindi, English, and medical terminology',
      'Doctor communication style: knows how Indian doctors write prescriptions and discharge summaries',
    ],
    footer: 'This isn\'t a feature — it\'s the foundation. Every other capability of RichHealth.ai is built on top of an AI that genuinely understands Indian health. No competitor can replicate years of India-specific training data and medical context.',
  },

  /* ---- Trust / Privacy ---- */
  'privacy': {
    title: 'How We Protect Your Data',
    subtitle: 'Privacy isn\'t a feature — it\'s the foundation',
    body: 'Your health data is the most sensitive information you have. We treat it that way. RichHealth.ai is built with privacy-first architecture from the ground up — because we believe earning your trust with health data is a privilege, not a business model.',
    highlights: [
      'AES-256 encryption at rest, TLS 1.3 in transit',
      'Zero data selling — ever. Your health data is never monetized',
      'No third-party analytics on personal health records',
      'Regular security audits and penetration testing',
      'HIPAA-aligned practices for international standards',
    ],
    footer: 'Your data belongs to you. We just help you make it intelligent.',
  },
  'mental-health': {
    title: 'Private Mental Health Chat',
    subtitle: 'A safe space for your most sensitive conversations',
    body: 'Mental health conversations with Richie are treated with the highest level of privacy. We understand the cultural sensitivities around mental health in India, and Richie is built to be a judgment-free, culturally aware companion — available 24/7 without stigma.',
    highlights: [
      'No conversation data used for model training',
      'No profiling or behavioral tracking',
      'Data retained only as long as you choose',
      'Culturally aware of Indian mental health context',
      'Available anytime, without stigma or judgment',
    ],
    footer: 'Everyone deserves a safe space to talk about their mental health. Richie provides that, without compromise.',
  },
  'data-control': {
    title: 'Your Data, Your Rules',
    subtitle: 'Complete sovereignty over your health information',
    body: 'Data control at RichHealth.ai isn\'t just a settings page — it\'s a philosophy. You have granular power over every piece of health information, who can see it, and how Richie uses it.',
    highlights: [
      'Export all your data as structured files, anytime',
      'One-tap permanent deletion — no retention, no recovery',
      'Granular family sharing controls — choose what each member sees',
      'Selective AI access — decide what Richie can reference',
      'Transparent data usage logs you can audit',
    ],
    footer: 'We don\'t just promise data control — we build it into every interaction.',
  },

  /* ---- Market ---- */
  'market': {
    title: 'Why This Matters, Right Now',
    subtitle: 'The gap that RichHealth.ai fills',
    body: 'India has 1.4 billion people, most on Android, most without access to quality healthcare. The digital health market is projected to reach $9.9 billion by 2029. But the real opportunity isn\'t just market size — it\'s the gap. No AI health platform is built for the Indian context.',
    highlights: [
      'No health AI understands Indian dietary patterns, regional diseases, or local medications',
      'No family-connected health intelligence exists anywhere in the world',
      'UPI-native payments for seamless subscription adoption',
      'Regional health concerns — pollution, dengue, TB — addressed natively',
      'Pricing calibrated for Indian purchasing power',
      'Southeast Asia, Africa, and Latin America share the same unmet needs',
    ],
    footer: 'RichHealth.ai isn\'t entering a market — it\'s creating a category.',
  },

  /* ---- Pricing ---- */
  'pricing-basic': {
    title: 'Basic Plan — Your Personal Health AI',
    subtitle: 'Everything you need to start building your health profile',
    body: 'The Basic plan gives you full access to Richie for personal health management. Track symptoms, medications, measurements, and get AI-powered health insights — all calibrated for Indian health context.',
    highlights: [
      'Richie AI chat with standard models',
      'Complete symptom & measurement tracking',
      '3 AI report analyses per month',
      'AQI environmental monitoring',
      'NutriCheck personalized food analysis',
      'Full medication management',
      'Location-based health news',
    ],
    footer: 'Start building your health intelligence profile today — at a price designed for India.',
  },
  'pricing-pro': {
    title: 'Pro Plan — Family Health Intelligence',
    subtitle: 'Connect your family. Multiply your health insights.',
    body: 'The Pro plan unlocks RichHealth.ai\'s most powerful differentiator: Family Health Network. Connect up to 3 family members and let Richie build cross-referential health intelligence across your entire family — the kind of hereditary insight no doctor visit provides.',
    highlights: [
      'Everything in Basic',
      'Premium AI models (Gemini, Llama 70B+)',
      'Up to 3 family members connected',
      '20 AI report analyses per month',
      'Family health network with hereditary risk detection',
      'Doctor connection portal',
      'Priority support',
    ],
    footer: 'The most popular plan for families serious about proactive health management.',
  },
  'pricing-ultra': {
    title: 'Ultra Plan — Complete Health Command Center',
    subtitle: 'Maximum intelligence. Maximum coverage.',
    body: 'The Ultra plan is for families that want the absolute best in AI-powered health management — extended family coverage, doctor-verified AI analyses, and white-glove support for complete peace of mind.',
    highlights: [
      'Everything in Pro',
      '6+ family members connected',
      '100 AI report analyses per month',
      'Doctor review of every AI analysis',
      'Custom podcast requests',
      'Dedicated health insights dashboard',
      'White-glove onboarding and support',
    ],
    footer: 'For families that believe health intelligence is the most important investment they can make.',
  },

  /* ---- Doctor Application ---- */
  'doctor-apply': {
    title: 'Become a RichHealth.ai Doctor',
    subtitle: 'Review AI reports. Build your reputation. Earn equity in the future of healthcare.',
    body: 'RichHealth.ai is building the world\'s first AI-verified health intelligence platform — and we need exceptional doctors to make it work. As a RichHealth.ai Doctor, you review Richie\'s AI-generated report analyses and provide the clinical oversight that transforms AI output into trusted medical intelligence. This isn\'t a side gig — it\'s an opportunity to shape how 1.4 billion people access healthcare, and earn a stake in that future.',
    highlights: [
      'Review AI-analyzed medical reports from your specialization — on your own schedule',
      'Upload your medical degree, license, and credentials for verification',
      'Your expertise trains Richie to become better — your feedback directly improves the AI',
      'Earn per review, with performance bonuses tied to quality and consistency',
      'Top-performing doctors earn equity stake in RichHealth.ai — real ownership in the platform',
      'Build a verified digital reputation as an early adopter of AI-assisted healthcare',
      'Flexible commitment — review 5 reports a week or 50, it\'s up to you',
    ],
    footer: 'Senior doctors with 20+ years of clinical experience are invited to email us directly at doctors@richhealth.app for priority onboarding, enhanced equity terms, and advisory board consideration.',
    ctaText: 'Check Eligibility',
    ctaHref: 'mailto:doctors@richhealth.app?subject=Doctor%20Application%20-%20RichHealth.ai',
  },
};

/* ---- Richie Chat Suggested Prompts ---- */
export const RICHIE_PROMPTS = [
  {
    question: 'What can you do?',
    answer: 'I analyze your medical reports, track symptoms and medications, monitor how air quality affects your health, and connect your family\'s health data for hereditary insights. I understand Indian dietary patterns, regional diseases, and local medication brands like Crocin, Dolo-650, and more. The more you share, the smarter I get about YOUR health specifically.',
  },
  {
    question: 'Am I at risk for diabetes?',
    answer: 'To give you a personalized risk assessment, I\'d cross-reference your family history, recent blood work, BMI trends, and lifestyle patterns. For South Asian populations, I factor in genetic predisposition and dietary context — dal, rice, and ghee intake patterns matter. In the app, this analysis happens automatically with your connected health data.',
  },
  {
    question: 'Analyze my blood report',
    answer: 'Upload any medical report — CBC, lipid panel, thyroid function, HbA1c — and I\'ll extract key findings, flag abnormal values, and cross-reference against your complete health profile and family genetics. Pro users get doctor verification on top of my AI analysis for clinical confidence.',
  },
  {
    question: 'How\'s the air quality today?',
    answer: 'I track your location\'s AQI in real-time using both US and China standards. I monitor PM2.5, PM10, and O\u2083 levels, and I\'ll alert you when pollution spikes could affect your health — especially if you have respiratory conditions or relevant family history. I also bring you local health news and outbreak alerts.',
  },
];
